<template>
  <!-- prettier-ignore -->
  <div class="PersonEdit">
    <div class="field">
      <Form.Location
        ref="zipchooser"
        v-model="formData.address"
        class="location"
        :autofocus="!formData.address.zipCode"
        name="location"
        required
        replace
        :contract-start-date="basket.contractStartDate"
        theme="light"
        :title="getLabel('person_location_label')"
        :validate="v.address"
      />
    </div>
    <div class="field">
      <Form.DateInput
        v-model="formData.dateOfBirth"
        class="person__input person__dob"
        is-active
        name="dateOfBirth"
        :delay="0"
        theme="light"
        :title="getLabel('person_birthdate_label')"
        :validate="v.dateOfBirth"
      />
    </div>
    <div class="field">
      <Form.Gender
        v-model="formData.gender"
        v-tooltip="getLabel('person_gender_tooltip')"
        class="person__input person__gender"
        name="gender"
        :custom-label-female="getLabel('gender_female')"
        :custom-label-male="getLabel('gender_male')"
        :custom-label-unborn="getLabel('gender_unborn')"
        :age="age"
        :validate="v.gender"
      />
    </div>
    <Form.Button
      class="button-validate"
      primary
      :text="getLabel('person_button')"
      @click="save"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, watch } from 'vue'
import { events$ } from '@/services'
import { isFuture, parseISO } from 'date-fns'

import * as Form from '@/components/Form'

import { DateTimeHelper } from '@/helpers'
import { getRouteData } from '@/modules/PartnerWidget/helpers/tracking'

import validations from '@/validations/personEdit'
import useVuelidate from '@vuelidate/core'

import { GENDER } from '@/config/constants'
import { EVENT_PARTNER_WIDGET, EVENT_PERSONS_FORM_DISPLAYED, SOURCE } from '@/config/events'

import usePartnerWidget from '@/hooks/usePartnerWidget'
import usePartnerWidgetBundleLoader from '@/hooks/usePartnerWidgetBundleLoader'

const { getLabel } = usePartnerWidget()
const { bundleConfig } = usePartnerWidgetBundleLoader()

// Init
const emit = defineEmits(['save'])
const props = defineProps({
  basket: {
    type: Object,
    default: () => ({}),
  },
})

// Data
const formData = reactive({
  address: {},
  gender: null,
  dateOfBirth: null,
})
const v = useVuelidate(validations, formData, { $lazy: true })

// Computed
const age = computed(() => {
  return DateTimeHelper.getAge(formData.dateOfBirth)
})

const isUnborn = computed(() => {
  return isUnbornFunc(formData.dateOfBirth)
})

// Methods
function save() {
  v.value.$touch()
  if (v.value.$invalid) {
    return
  }
  emit('save', formData)
}

function trackPersonScreenShown() {
  const pageData = getRouteData({
    title: 'PersonEdit',
    routes: ['person', 'edit'],
  })
  if (bundleConfig.skipPackagesOverview && bundleConfig.skipEntryPage) {
    events$.emit(EVENT_PARTNER_WIDGET.DISPLAYED, pageData)
  }
  events$.emit(EVENT_PERSONS_FORM_DISPLAYED, {
    source: SOURCE.PARTNER_WIDGET,
    trackingData: pageData,
  })
}

function isUnbornFunc(date) {
  return date && date.length === 10 && isFuture(parseISO(date))
}

// Lifecycle hooks
onMounted(() => {
  const address = props.basket.address
  const personData = props.basket.persons[0].personData
  if (address) {
    formData.address = { ...address }
  }
  if (personData) {
    formData.dateOfBirth = personData.dateOfBirth
    formData.gender = personData.gender
  }
  trackPersonScreenShown()
})

// Watchers
watch(
  () => isUnborn,
  value => {
    // if dateOfBirth changed from unborn, reset the gender
    if (!value && formData.gender === GENDER.UNBORN) {
      formData.gender = null
    }
    // if dateOfBirth changed to unborn, force the gender to UNBORN
    if (value) {
      formData.gender = GENDER.UNBORN
    }
  }
)
</script>

<style scoped>
.PersonEdit {
  .field {
    margin-bottom: 20px;
  }

  .button-validate {
    .narrow & {
      width: 100%;
    }
  }
}
</style>

<style name="short" scoped>
@media screen and (max-height: 596px) {
  .PersonEdit .field {
    margin-bottom: 10px;
  }
}
</style>
